import {authStorage} from '@features/auth/api/authStorage';
import {userIdStorage} from '@features/auth/api/userIdStorage';
import {agencyStorage} from '@features/auth/api/agencyStorage';
import {timeZoneStorage} from '@features/auth/api/timeZoneStorage';

/**
 * What the axios interceptor would have attached, for the calls that cannot use it.
 *
 * Nova is reached with plain `fetch` in three places — the streamed chat, and
 * the two voice uploads — because each needs something axios is in the way of:
 * a readable body stream, or a `FormData` boundary axios would overwrite.
 * Every one of them therefore has to rebuild these headers, and every one of
 * them got it wrong at least once.
 *
 * It lives in one file because the failure is silent and identical each time:
 * a 401 that looks like a broken session, or worse, a request answered for the
 * wrong tenant because `X-Agency-Id` was missing.
 *
 * Deliberately **no `Content-Type`.** A `FormData` body must set its own, with
 * the multipart boundary; supplying one here would corrupt the upload. Callers
 * that send JSON add it themselves.
 *
 * `credentials: 'include'` is the caller's job and is not optional on web: the
 * technician's session is an HttpOnly cookie, and without it nothing carries
 * the token at all.
 */
export async function novaAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  const token = await authStorage.getAccessToken();
  // The sentinel is a navigation marker, not a token — the same check the
  // axios interceptor makes. On the web cookie flow there is no bearer to
  // send and the HttpOnly cookie does the work.
  if (token && token !== 'ttauth-cookie-session') {
    headers.Authorization = `Bearer ${token}`;
  }

  const userId = userIdStorage.get();
  if (userId !== null) {
    headers['X-UserId'] = String(userId);
  }

  const agencyId = agencyStorage.get();
  if (agencyId !== null) {
    headers['X-Agency-Id'] = agencyId;
  }

  const timeZoneId = timeZoneStorage.get();
  if (timeZoneId !== null) {
    headers['X-TimeZone'] = timeZoneId;
  }

  return headers;
}
